import { type ButtonVariantType, type RequirementIdentifier, useIntl } from '@capsicum/common';
import { useCallback, useMemo } from 'react';

import { useRequirementStatuses } from './useRequirementStatuses';

export const useCustomRequirementsSummary = () => {
	const { $t } = useIntl();
	const { currentRequirement, currentRequirementStatus, getStatus, kycSteps, userIsBlocked } =
		useRequirementStatuses();

	const isRequirementDisabled = useCallback(
		(requirement: RequirementIdentifier) => {
			const status = getStatus(requirement);
			const isCurrentRequirement = requirement === currentRequirement;

			return status === 'completed' || status === 'underReview' || !isCurrentRequirement;
		},
		[getStatus, currentRequirement]
	);

	const shouldShowDescription = useCallback(
		(requirement: RequirementIdentifier) => !userIsBlocked && requirement === currentRequirement,
		[userIsBlocked, currentRequirement]
	);

	const getStepIcon = useCallback(
		({ requirement, step }: { requirement: RequirementIdentifier; step: number }) => {
			const status = getStatus(requirement);

			return status === 'notStarted' ? step : status;
		},
		[getStatus]
	);

	const continueButtonText = useMemo(() => {
		const isCurrentRequirementOnRetry = currentRequirementStatus === 'retry';

		return $t(`global.button.${isCurrentRequirementOnRetry ? 'retry' : 'continue'}`);
	}, [currentRequirementStatus]);

	const cancelButtonText = useMemo(() => $t(`global.button.${userIsBlocked ? 'back' : 'cancel'}`), [userIsBlocked]);
	const cancelButtonVariant = useMemo<ButtonVariantType | undefined>(
		() => (userIsBlocked ? undefined : 'outlined'),
		[userIsBlocked]
	);

	return {
		cancelButtonText,
		cancelButtonVariant,
		continueButtonText,
		getStepIcon,
		isRequirementDisabled,
		kycSteps,
		shouldShowDescription,
		userIsBlocked
	};
};
